import { useState, useRef, useEffect } from "react";
import { StepIndicator } from "@/components/StepIndicator";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AvailabilityGate, type AvailabilityFormData } from "@/components/audition/AvailabilityGate";
import { AudioTestStep } from "@/components/audition/AudioTestStep";
import { GenreAssessment, type GenreAssessmentData } from "@/components/audition/GenreAssessment";
import { AuditionConfirmation } from "@/components/audition/AuditionConfirmation";
import { Logo } from "@/components/Logo";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Music, Clock, Shield } from "lucide-react";
import { PageTransition } from "@/components/PageTransition";
import { getReferenceTrackUrl } from "@/lib/referenceTrackGenerator";

const STEPS = ["Welcome", "Availability", "Test A", "Test B", "Test C", "Genre", "Done"];

const PERCUSSION_INSTRUMENTS = [
  "Drums", "Drumset", "Snare Drum", "Tabla", "Dhol", "Djembe", "Handpan", "Percussion", "Cajon"
];

const getAudioTests = (instrument?: string) => {
  const isPercussion = instrument && PERCUSSION_INSTRUMENTS.some(i => 
    instrument.toLowerCase().includes(i.toLowerCase())
  );

  return [
    {
      testKey: isPercussion ? "lock_in_percussion" : "lock_in",
      title: isPercussion 
        ? "Test A: Groove Lock-In" 
        : "Test A: Copy-Cat — Just the Two of Us",
      prompt: isPercussion
        ? `Listen to the drum groove pattern. When ready, hit Record and lock in with the beat, then add your own flavor.\n\nThe track will NOT play during recording — keep the tempo steady!`
        : `Listen to our rendition of "Just the Two of Us" as many times as you need.\nWhen ready, hit Record and reproduce what you heard as accurately as possible.\n\nThe track will NOT play during recording — this tests your ear, memory, and musical accuracy.`,
      maxDuration: 45,
      mode: "copycat" as const,
    },
    {
      testKey: "improvisation",
      title: "Test B: Play Along — Autumn Leaves",
      prompt: `"Autumn Leaves" will play in your headphones while you record.\nPlay along but add your own tasteful variations — embellishments, fills, or subtle reharmonizations.\n\n🎧 Headphones required so the mic only captures your instrument.`,
      maxDuration: 45,
      mode: "play-along" as const,
    },
    {
      testKey: "follow_direction",
      title: "Test C: Play Along — Amazing Grace",
      prompt: `"Amazing Grace" will play in your headphones while you record.\nThis time, don't play the melody — create a countermelody or harmony part that complements the hymn.\n\n🎧 Headphones required so the mic only captures your instrument.`,
      maxDuration: 45,
      mode: "play-along" as const,
    },
  ];
};

const Audition = () => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const startTimeRef = useRef<number>(Date.now());

  // Reference track URLs
  const [refUrls, setRefUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    // Pre-generate all reference tracks
    const keys = ["lock_in", "lock_in_percussion", "improvisation", "follow_direction"] as const;
    keys.forEach((key) => {
      getReferenceTrackUrl(key).then((url) =>
        setRefUrls((prev) => ({ ...prev, [key]: url }))
      );
    });
  }, []);

  // Data collection
  const [availabilityData, setAvailabilityData] = useState<AvailabilityFormData | null>(null);
  const [recordings, setRecordings] = useState<Record<string, { blob: Blob; duration: number }>>({});
  const [genreData, setGenreData] = useState<GenreAssessmentData | null>(null);

  const handleAvailabilityComplete = (data: AvailabilityFormData) => {
    setAvailabilityData(data);
    setStep(3);
  };

  const currentTests = getAudioTests(availabilityData?.primary_instrument);

  const handleAudioComplete = (testKey: string, blob: Blob, duration: number) => {
    setRecordings(prev => ({ ...prev, [testKey]: { blob, duration } }));
  };

  const handleGenreComplete = async (data: GenreAssessmentData) => {
    setGenreData(data);
    await submitAudition(data);
  };

  const submitAudition = async (genreInfo: GenreAssessmentData) => {
    if (!availabilityData) return;
    setIsSubmitting(true);

    try {
      const completionTimeMs = Date.now() - startTimeRef.current;

      // Upload audio files to storage first (parallel)
      const uploadedPaths: Record<string, { path: string; duration: number }> = {};
      
      const uploadPromises = Object.entries(recordings).map(async ([key, { blob, duration }]) => {
        const fileName = `submissions/${Date.now()}_${crypto.randomUUID()}_${key}.webm`;
        const { error: uploadError } = await supabase.storage
          .from("audition-recordings")
          .upload(fileName, blob, { contentType: "audio/webm", upsert: true });

        if (uploadError) {
          console.error(`Upload error for ${key}:`, uploadError);
          return;
        }
        uploadedPaths[key] = { path: fileName, duration };
      });

      await Promise.all(uploadPromises);

      // Send JSON payload to edge function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-audition`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            "Authorization": `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            availability: availabilityData,
            genre_data: genreInfo,
            completion_time_ms: completionTimeMs,
            audio_paths: uploadedPaths,
          }),
        }
      );

      if (!response.ok) {
        const errBody = await response.text();
        throw new Error(errBody || `Server error ${response.status}`);
      }

      const data = await response.json();

      setStep(7);
      toast({ title: "Audition submitted!", description: "We'll be in touch soon." });
    } catch (err: any) {
      console.error("Submit error:", err);
      toast({
        title: "Submission failed",
        description: err.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedFromAudioTest = (testIndex: number) => {
    const testKey = currentTests[testIndex].testKey;
    return !!recordings[testKey];
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b bg-background/80 backdrop-blur-lg sticky top-0 z-50">
          <div className="container mx-auto px-4 py-3 flex justify-between items-center">
            <Logo size="md" />
            <StepIndicator currentStep={step} totalSteps={STEPS.length} steps={STEPS} />
          </div>
        </header>

        <main className="container mx-auto px-4 py-8 max-w-2xl">
          {/* Step 1: Welcome */}
          {step === 1 && (
            <Card>
              <CardContent className="pt-8 space-y-6">
                <div className="text-center space-y-4">
                  <div className="mx-auto w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                    <Music className="h-8 w-8 text-primary" />
                  </div>
                  <h1 className="text-3xl font-bold">Elite Musician Vetting</h1>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    The Lost Strings certifies musicians as luxury-bookable professionals — 
                    not just skilled players — in under 10 minutes.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                  <div className="text-center p-4 rounded-lg bg-muted/50 border">
                    <Clock className="h-5 w-5 mx-auto mb-2 text-primary" />
                    <p className="text-sm font-medium">8–10 Minutes</p>
                    <p className="text-xs text-muted-foreground">Total time</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-muted/50 border">
                    <Music className="h-5 w-5 mx-auto mb-2 text-primary" />
                    <p className="text-sm font-medium">3 Audio Tests</p>
                    <p className="text-xs text-muted-foreground">Single take each</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-muted/50 border">
                    <Shield className="h-5 w-5 mx-auto mb-2 text-primary" />
                    <p className="text-sm font-medium">Tier Placement</p>
                    <p className="text-xs text-muted-foreground">A, B, or C</p>
                  </div>
                </div>

                <div className="bg-muted/30 rounded-lg p-4 border text-sm text-muted-foreground space-y-2">
                  <p><strong>What you'll need:</strong></p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Your instrument ready to play</li>
                    <li>A quiet environment</li>
                    <li>Working microphone (phone or computer)</li>
                    <li>Headphones recommended (to hear reference tracks)</li>
                  </ul>
                </div>

                <Button onClick={() => setStep(2)} className="w-full" size="lg">
                  Begin Audition →
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Availability Gate */}
          {step === 2 && (
            <Card>
              <CardContent className="pt-6">
                <AvailabilityGate onComplete={handleAvailabilityComplete} />
              </CardContent>
            </Card>
          )}

          {/* Steps 3-5: Audio Tests */}
          {step >= 3 && step <= 5 && (
            <Card>
              <CardContent className="pt-6 space-y-4">
                <AudioTestStep
                  key={currentTests[step - 3].testKey}
                  {...currentTests[step - 3]}
                  referenceTrackUrl={refUrls[currentTests[step - 3].testKey]}
                  mode={currentTests[step - 3].mode}
                  onComplete={handleAudioComplete}
                  isCompleted={canProceedFromAudioTest(step - 3)}
                />
                {canProceedFromAudioTest(step - 3) && (
                  <Button
                    onClick={() => setStep(step + 1)}
                    className="w-full"
                    size="lg"
                  >
                    {step < 5 ? "Next Test →" : "Continue to Genre Assessment →"}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 6: Genre Assessment */}
          {step === 6 && (
            <Card>
              <CardContent className="pt-6">
                {isSubmitting ? (
                  <div className="text-center py-12 space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
                    <p className="text-muted-foreground">Submitting your audition...</p>
                  </div>
                ) : (
                  <GenreAssessment onComplete={handleGenreComplete} />
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 7: Confirmation */}
          {step === 7 && <AuditionConfirmation />}
        </main>
      </div>
    </PageTransition>
  );
};

export default Audition;
